import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { ExpenseExternalPartySelect } from '@/components/shared/expense-external-party-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { api } from '@/lib/api';

export type ContributionGiverKind = 'internal' | 'external' | 'other';

const GIVER_KINDS: ContributionGiverKind[] = ['internal', 'external', 'other'];

interface Member {
  id: string;
  firstName: string;
  lastName: string;
}

export interface ContributionGiverFieldsProps {
  open: boolean;
  kind: ContributionGiverKind;
  onKindChange: (kind: ContributionGiverKind) => void;
  memberId: string | null;
  onMemberIdChange: (memberId: string | null) => void;
  externalPartyId: string | null;
  onExternalPartyChange: (partyId: string | null) => void;
  giverName: string;
  onGiverNameChange: (giverName: string) => void;
  /** Church member can be left blank. Company and other still need a name. */
  memberOptional?: boolean;
}

export const ContributionGiverFields = ({
  open,
  kind,
  onKindChange,
  memberId,
  onMemberIdChange,
  externalPartyId,
  onExternalPartyChange,
  giverName,
  onGiverNameChange,
  memberOptional = true,
}: ContributionGiverFieldsProps) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const [memberQuery, setMemberQuery] = useState('');

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', 'contribution-giver', memberQuery],
    queryFn: () =>
      api.get<{ data: Member[] }>(
        `/people/members?pageSize=20${memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''}`,
      ),
    enabled: open && kind === 'internal',
    placeholderData: keepPreviousData,
  });

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [members.data?.data],
  );
  const selectedMember = memberOptions.find((option) => option.id === memberId) ?? null;

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label>{t('form.giverType')}</Label>
        <Listbox
          value={kind}
          onChange={(next) => onKindChange((next as ContributionGiverKind) ?? 'internal')}
          options={GIVER_KINDS.map((value) => ({
            value,
            label: t(`form.giverKinds.${value}`),
          }))}
          popoverPortal="dialog"
        />
      </div>
      {kind === 'internal' ? (
        <div className="flex flex-col gap-1.5">
          <Label>
            {t('form.person')}
            {memberOptional ? (
              <span className="text-fg-secondary"> ({t('form.memberOptional')})</span>
            ) : null}
          </Label>
          <EntityCombobox
            value={memberId}
            onChange={onMemberIdChange}
            options={memberOptions}
            selected={selectedMember}
            onQueryChange={setMemberQuery}
            loading={members.isPending && !members.data}
            popoverPortal="dialog"
            clearLabel={tCommon('action.clearSelection')}
            placeholder={t('form.personPlaceholder')}
          />
        </div>
      ) : null}
      {kind === 'external' ? (
        <div className="flex flex-col gap-1.5">
          <Label>{t('form.organisation')}</Label>
          <ExpenseExternalPartySelect
            value={externalPartyId}
            onChange={(partyId) => onExternalPartyChange(partyId)}
            popoverPortal="dialog"
          />
        </div>
      ) : null}
      {kind === 'other' ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="contribution-giver-name">{t('form.otherGiver')}</Label>
          <Input
            id="contribution-giver-name"
            value={giverName}
            onChange={(event) => onGiverNameChange(event.target.value)}
            placeholder={t('form.otherGiverPlaceholder')}
            required
          />
        </div>
      ) : null}
    </>
  );
};

export const contributionGiverPayload = (input: {
  kind: ContributionGiverKind;
  memberId: string | null;
  externalPartyId: string | null;
  giverName: string;
}) => ({
  giverKind: input.kind,
  memberId: input.kind === 'internal' ? input.memberId : null,
  externalPartyId: input.kind === 'external' ? input.externalPartyId : null,
  giverName: input.kind === 'other' ? input.giverName.trim() || null : null,
});

export const contributionGiverReady = (input: {
  kind: ContributionGiverKind;
  memberId: string | null;
  externalPartyId: string | null;
  giverName: string;
  memberRequired?: boolean;
}) => {
  if (input.kind === 'internal') return input.memberRequired ? Boolean(input.memberId) : true;
  if (input.kind === 'external') return Boolean(input.externalPartyId);
  return Boolean(input.giverName.trim());
};
